import React from 'react';
import { X, Download, Calendar, User, Tag } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MediaPlayer = ({ media, isOpen, onClose, onDownload }) => {
  if (!media) return null;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getCategoryLabel = (slug) => {
    const labels = {
      'sunday-service': 'Sunday Service',
      'bible-study': 'Bible Study',
      'special-events': 'Special Events',
      'youth-ministry': 'Youth Ministry'
    };
    return labels[slug] || slug;
  };

  const streamUrl = `${API_URL}/api/media/${media.id}/stream`;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-full p-0 overflow-hidden" data-testid="media-player-modal">
        <DialogHeader className="p-6 pb-0">
          <div className="flex items-start justify-between">
            <div className="flex-1 pr-8">
              <Badge variant="secondary" className="mb-2 uppercase text-xs tracking-wider">
                {media.media_type}
              </Badge>
              <DialogTitle className="font-serif text-2xl md:text-3xl font-semibold">
                {media.title}
              </DialogTitle>
            </div>
          </div>
        </DialogHeader>
        
        <div className="px-6 pb-6">
          {/* Media Player */}
          <div className="bg-black rounded-sm overflow-hidden mb-6">
            {media.media_type === 'video' ? (
              <video 
                controls 
                className="w-full max-h-[60vh]"
                src={streamUrl}
                poster={media.thumbnail_url}
                data-testid="video-player"
              >
                Your browser does not support the video element.
              </video>
            ) : (
              <div className="p-8 flex flex-col items-center gap-4">
                <img 
                  src={media.thumbnail_url} 
                  alt={media.title}
                  className="w-48 h-48 object-cover rounded-sm shadow-lg"
                />
                <audio 
                  controls 
                  className="w-full max-w-md"
                  src={streamUrl}
                  data-testid="audio-player"
                >
                  Your browser does not support the audio element.
                </audio>
              </div>
            )}
          </div>

          {/* Media Info */}
          <div className="space-y-4">
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(media.service_date)}</span>
              </div>
              {media.speaker && (
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  <span>{media.speaker}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Tag className="w-4 h-4" />
                <span>{getCategoryLabel(media.category)}</span>
              </div>
            </div>
            
            {media.description && (
              <p className="text-foreground leading-relaxed">
                {media.description}
              </p>
            )}

            <div className="flex gap-3 pt-4 border-t border-border">
              <Button 
                variant="default"
                onClick={() => onDownload(media)}
                data-testid="modal-download-button"
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
              <Button 
                variant="outline"
                onClick={onClose}
                data-testid="modal-close-button"
              >
                <X className="w-4 h-4 mr-2" />
                Close
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MediaPlayer;
